import { Box, Divider, styled, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { LwButton } from 'redesign';
import { useGetDepartmentDetail } from 'api/hooks/department/use-get-department-detail';
import { ShiftDetail } from 'api/services/shift/schema/shift-detail.schema';
import { useGetWorkCreateData, useGetWorkTemplate, useGetWorkTemplates } from 'api';
import { useSiteDetection } from 'shared/hooks';
import { useEffect, useMemo } from 'react';
import { useGetShiftDetail } from 'api/hooks/shift/get-shift-detail';
import { useTranslation } from 'react-i18next';
import { WorkerEmploymentType } from '@types';
import { WorkForm } from './work-form';
import { WorkFormCompanyInfo, WorkFormContext, WorkFormMode } from './work-form.types';
import { mapShiftDetailToForm, mapWorkTemplateToForm } from './helpers/mappers';
import { useWorkFormTexts } from './hooks/use-work-form-texts';
import { useWorkFormMutations } from './hooks/use-work-form-mutations';
import { getHelperTexts } from '../shift-forms/components/forms/shared/get-helper-texts/get-helper-texts';
import { RenderUnsettledUI } from '../render-unsettled-ui';

export type WorkFormProviderProps = {
  cancel: () => void;
  companies: WorkFormCompanyInfo[];
  formMode: WorkFormMode;
  selectedShiftIds: string[];
  isHqAdmin: boolean;
  onSuccess: (shiftId: string) => void;
};

export type WorkFormDataProviderProps = {
  cancel: () => void;
  companies: WorkFormCompanyInfo[];
  formMode: WorkFormMode;
  isHqAdmin: boolean;
  onSuccess: (shiftId: string) => void;
  shiftId: string | undefined;
  shiftData: ShiftDetail | undefined;
};

export const WorkFormProvider = ({
  cancel,
  companies,
  formMode,
  selectedShiftIds,
  isHqAdmin,
  onSuccess,
}: WorkFormProviderProps) => {
  const shiftId = formMode != 'create' ? selectedShiftIds[0] : undefined;
  const shiftQuery = useGetShiftDetail(shiftId);

  if (shiftId && shiftQuery.status !== 'success') {
    return <RenderUnsettledUI data={shiftQuery} />;
  }

  return (
    <WorkFormDataProvider
      cancel={cancel}
      companies={companies}
      formMode={formMode}
      isHqAdmin={isHqAdmin}
      onSuccess={onSuccess}
      shiftData={shiftQuery?.data}
      shiftId={shiftId}
    />
  );
};

export const WorkFormDataProvider = ({
  cancel,
  companies,
  formMode,
  isHqAdmin,
  onSuccess,
  shiftData,
  shiftId,
}: WorkFormDataProviderProps) => {
  // Form
  const formMethods = useForm<WorkFormContext>({
    defaultValues: {
      location: null,
      companyOption: null,
      ...mapShiftDetailToForm(shiftData),
    },
    shouldUnregister: false,
  });
  const selectedCompany = formMethods.watch('companyOption');
  const selectedCompanyId = selectedCompany?.value;
  const selectedDepartmentId = formMethods.watch('department');
  const selectedWorkTemplateId = formMethods.watch('jobId');
  const selectedJobTypeId = formMethods.watch('jobType');
  const isTempWork = formMethods.watch('isTempwork');
  const isFreelance = formMethods.watch('isFreelance');
  const certificates = formMethods.watch('jobCertificates');
  const startDate = formMethods.watch('startDate');
  const endDate = formMethods.watch('endDate');
  const startTime = formMethods.watch('startTime');
  const endTime = formMethods.watch('endTime');
  const shouldAdhereToDrivingRestRules =
    formMethods.watch('shouldAdhereToDrivingRestRules') ?? false;
  const nightShiftLimitationSystem = formMethods.watch('nightShiftLimitationSystem');

  const { site, isHQ, isOGP } = useSiteDetection();
  const workTemplatesQuery = useGetWorkTemplates(site, selectedCompanyId, !!selectedCompanyId);
  const shiftCreateDataQuery = useGetWorkCreateData(site, selectedCompanyId, !!selectedCompanyId);
  const workTemplateDataQuery = useGetWorkTemplate(
    site,
    selectedWorkTemplateId,
    !!selectedWorkTemplateId
  );
  const departmentDataQuery = useGetDepartmentDetail(
    site,
    selectedDepartmentId,
    !!selectedDepartmentId
  );
  const { t } = useTranslation(['web-common', 'hq-screen-specific']);

  const helperTexts = useMemo(
    () =>
      getHelperTexts(
        workTemplateDataQuery.data
          ? {
              breakMinutes: workTemplateDataQuery.data.breakMinutes,
              hourlyRateCents: workTemplateDataQuery.data.freelanceJob?.hourlyRateCents,
            }
          : undefined
      ),
    [
      workTemplateDataQuery.data?.breakMinutes,
      workTemplateDataQuery.data?.freelanceJob?.hourlyRateCents,
    ]
  );

  const [company] = companies;
  useEffect(() => {
    const [company] = companies;
    if (isOGP && company) {
      formMethods.setValue('companyOption', { value: company.id, label: company.name });
    }
  }, [company?.id, isOGP]);

  const defaultValues = useMemo(() => {
    if (formMode === 'edit' || formMode === 'duplicate') {
      return mapShiftDetailToForm(shiftData);
    }
    return mapWorkTemplateToForm(workTemplateDataQuery.data);
  }, [formMode, shiftData?.id, workTemplateDataQuery.data?.id]);

  const applicationsAndPlacementsEmploymentTypes: WorkerEmploymentType[] = useMemo(() => {
    if (formMode !== 'edit' || !shiftData) {
      return [];
    }

    const shiftApplicants = shiftData.applicants
      .map((a) => a.flexWorker?.employmentType)
      .filter((t): t is WorkerEmploymentType => !!t);

    const shiftClaimants = shiftData.claimants
      .map((c) => c.flexWorker?.employmentType)
      .filter((t): t is WorkerEmploymentType => !!t);

    return [...shiftApplicants, ...shiftClaimants];
  }, [formMode, shiftData]);

  const { title, subtitle } = useWorkFormTexts(formMode);

  const showCompanySelect = isHQ && formMode === 'create';
  const showTemplateSelect = formMode === 'create';
  const isSubmitDisabled =
    (!selectedCompanyId && showCompanySelect) || (!selectedWorkTemplateId && showTemplateSelect);
  const disableOrtApplicableSwitches = !isHqAdmin && formMode === 'edit';

  const { onSubmit } = useWorkFormMutations({
    shiftId,
    formMode,
    defaultValues,
    cancel,
    onSuccess,
  });

  return (
    <FormContainer>
      <FormProvider {...formMethods}>
        <FullHeightForm
          name="work-form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            formMethods.handleSubmit(onSubmit)(e);
          }}
        >
          <FormBody>
            <Box>
              <Box>
                <Typography variant="h1" mb={4}>
                  {title}
                </Typography>
                <Typography variant="body1" mb={4}>
                  {subtitle}
                </Typography>
              </Box>
              <Divider />
              <WorkForm
                showCompanySelect={showCompanySelect}
                showTemplateSelect={showTemplateSelect}
                isContractFieldDisabled={
                  !isHqAdmin &&
                  formMode === 'edit' &&
                  !!shiftData?.claimants.filter((claimant) => claimant.state === 'accepted').length
                }
                allowDateBeforeToday={
                  isHqAdmin || formMode === 'create' || formMode === 'duplicate'
                }
                companies={companies}
                workTemplates={workTemplatesQuery.data}
                shiftCreateData={shiftCreateDataQuery.data}
                defaultValues={defaultValues}
                departmentData={departmentDataQuery.data}
                showMultiplySection={formMode === 'create' || formMode === 'duplicate'}
                multiplySectionAllowsPast={isHqAdmin}
                helperTexts={helperTexts}
                disableOrtApplicableSwitches={disableOrtApplicableSwitches}
                applicationsAndPlacementsEmploymentTypes={applicationsAndPlacementsEmploymentTypes}
                shouldAdhereToDrivingRestRules={shouldAdhereToDrivingRestRules}
                nightShiftLimitationSystem={nightShiftLimitationSystem}
              />
            </Box>
          </FormBody>

          <SidebarActions>
            <LwButton onClick={cancel} color="secondary">
              Sluiten
            </LwButton>
            <LwButton color="primary" type="submit" disabled={isSubmitDisabled}>
              {formMode === 'edit'
                ? t('buttons.save', { ns: 'web-common' })
                : t('buttons.create', { ns: 'web-common' })}
            </LwButton>
          </SidebarActions>
        </FullHeightForm>
      </FormProvider>
    </FormContainer>
  );
};

const FormContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(8),
}));

const FullHeightForm = styled('form')(() => ({
  height: '100%',
}));

const FormBody = styled(Box)(({ theme }) => ({
  width: '650px',
  overflow: 'auto',
  paddingRight: theme.spacing(2),
  flex: 1,
  height: 'calc(100% - 88px)',
  display: 'flex',
  flexDirection: 'column',
}));

const SidebarActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(8),
}));
